import { useCallback, useEffect, useRef, useState } from 'react';
import { getUserAvatarAssetGroup } from '../api/userAvatarAssetGroup';
import { useSeedanceLibraryStore } from '../store/seedanceLibraryStore';
import { useSeedanceUiStore } from '../store/seedanceUiStore';
import {
  buildVirtualAvatarGroupOptions,
  resolveVirtualAvatarGroupSelection,
  type VirtualAvatarGroupOption,
} from '../utils/avatarAssetGroup';
import { getLocalStorageValue, SEEDANCE_STORAGE_KEYS } from '../utils/seedanceStorage';

export function useVirtualAvatarGroups(active: boolean) {
  const addDebug = useSeedanceUiStore((s) => s.addDebug);
  const libSelectedGroupId = useSeedanceLibraryStore((s) => s.libSelectedGroupId);
  const setLibSelectedGroupId = useSeedanceLibraryStore((s) => s.setLibSelectedGroupId);
  const [options, setOptions] = useState<VirtualAvatarGroupOption[]>([]);
  const [loading, setLoading] = useState(false);
  const bootstrappedRef = useRef(false);

  const hasAuth = useCallback(() => {
    const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
    return Boolean(userId);
  }, []);

  const loadGroups = useCallback(async () => {
    if (!hasAuth()) {
      setOptions([]);
      setLibSelectedGroupId('');
      return;
    }
    setLoading(true);
    try {
      const customGroupId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.customGroupId).trim();
      let info;
      if (customGroupId) {
        info = {
          groupId: customGroupId,
        };
      } else {
        info = await getUserAvatarAssetGroup({
          onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
        });
      }
      const built = buildVirtualAvatarGroupOptions(info as any);
      setOptions(built);
      const current = useSeedanceLibraryStore.getState().libSelectedGroupId;
      setLibSelectedGroupId(resolveVirtualAvatarGroupSelection(built, current));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      addDebug(`获取虚拟人像素材组失败: ${msg}`, true);
      setOptions([]);
      setLibSelectedGroupId('');
    } finally {
      setLoading(false);
    }
  }, [addDebug, hasAuth, setLibSelectedGroupId]);

  useEffect(() => {
    if (!active) {
      bootstrappedRef.current = false;
      return;
    }
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    void loadGroups();
  }, [active, loadGroups]);

  const selectGroupId = useCallback(
    (groupId: string) => {
      setLibSelectedGroupId(groupId.trim());
    },
    [setLibSelectedGroupId],
  );

  return {
    options,
    loading,
    hasAuth: hasAuth(),
    selectedGroupId: libSelectedGroupId,
    selectGroupId,
    reload: loadGroups,
  };
}
